import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
    useGetFinancialStatsQuery,
    useAddDailyPercentMutation,
    useConfirmWithdrawAllMutation,
    useConfirmTransferAllMutation,
    useGetInvestmentWithdrawRequestsQuery,
    useGetInvestmentTransferRequestsQuery,
    useConfirmInvestmentWithdrawMutation,
    useCancelInvestmentWithdrawMutation,
    useConfirmInvestmentTransferMutation,
    useCancelInvestmentTransferMutation,
} from "../api";
import { FinancialStats } from "../components/FinancialStats";
import { InvestmentManagement } from "../components/InvestmentManagement";
import { InvestmentWithdrawRequests } from "../components/InvestmentWithdrawRequests";
import { InvestmentTransferRequests } from "../components/InvestmentTransferRequests";
import { InvestmentStatsTabs } from "../components/InvestmentStatsTabs";
import { InvestmentStrategiesAggregatedStats } from "../components/InvestmentStrategiesAggregatedStats";
import type { InvestmentWithdrawRequest } from "../types/investments";

export const StakingPage: React.FC = () => {
    const { user } = useSelector((state: RootState) => state.auth);
    const roles = user?.roles || [];
    const isAdmin = roles.includes("admin");
    const isEditorOnly = roles.includes("editor") && !isAdmin;
    const skipAdmin = isEditorOnly;

    const [dailyPercent, setDailyPercent] = useState("");
    const [selectedStrategy, setSelectedStrategy] = useState(0);

    // Загрузка финансовой статистики
    const {
        data: statsResponse,
        isLoading,
        refetch: refetchFinancialStats,
    } = useGetFinancialStatsQuery(undefined, {
        pollingInterval: 120000,
        skip: skipAdmin,
    });

    const stats = statsResponse?.data;

    // Мутации
    const [addPercent, { isLoading: isAddingPercent }] =
        useAddDailyPercentMutation();
    const [confirmWithdraw, { isLoading: isConfirmingWithdraw }] =
        useConfirmWithdrawAllMutation();
    const [confirmTransfer, { isLoading: isConfirmingTransfer }] =
        useConfirmTransferAllMutation();

    const {
        data: investmentWithdrawRequests,
        isFetching: isFetchingInvestmentWithdrawals,
        refetch: refetchInvestmentWithdrawals,
    } = useGetInvestmentWithdrawRequestsQuery(undefined, {
        pollingInterval: 120000,
        skip: skipAdmin,
    });
    const {
        data: investmentTransferRequests,
        isFetching: isFetchingInvestmentTransfers,
        refetch: refetchInvestmentTransfers,
    } = useGetInvestmentTransferRequestsQuery(undefined, {
        pollingInterval: 120000,
        skip: skipAdmin,
    });
    const [confirmInvestmentWithdraw] = useConfirmInvestmentWithdrawMutation();
    const [cancelInvestmentWithdraw] = useCancelInvestmentWithdrawMutation();
    const [confirmInvestmentTransfer] = useConfirmInvestmentTransferMutation();
    const [cancelInvestmentTransfer] = useCancelInvestmentTransferMutation();
    const [pendingInvestmentKey, setPendingInvestmentKey] = useState<
        string | null
    >(null);
    const [pendingTransferKey, setPendingTransferKey] = useState<
        string | null
    >(null);

    const handleConfirmWithdraw = async () => {
        // Вычисляем общую сумму pending выводов
        const totalWithdrawPending =
            stats?.investments
                ? Object.values(stats.investments).reduce(
                      (sum, strategy) =>
                          sum + (strategy.withdraw_pending || 0),
                      0
                  )
                : 0;

        if (totalWithdrawPending === 0) {
            window.alert("Нет средств для вывода");
            return;
        }

        const totalAfterCommission = totalWithdrawPending * 0.99;
        const commission = totalWithdrawPending * 0.01;

        const confirmMessage = `Вывести все средства из инвестиций?\n\n` +
            `Сумма к выводу: $${totalWithdrawPending.toFixed(2)}\n` +
            `Комиссия (1%): $${commission.toFixed(2)}\n` +
            `К зачислению: $${totalAfterCommission.toFixed(2)} USDT`;

        if (window.confirm(confirmMessage)) {
            try {
                await confirmWithdraw().unwrap();
                window.alert("Выводы подтверждены успешно");
                refetchFinancialStats();
            } catch (error: any) {
                window.alert("Ошибка: " + (error.data?.message || error.message));
            }
        }
    };

    const handleConfirmTransfer = async () => {
        if (window.confirm("Перевести все средства?")) {
            try {
                await confirmTransfer().unwrap();
                window.alert("Переводы подтверждены");
                refetchInvestmentTransfers();
                refetchFinancialStats();
            } catch (error: any) {
                window.alert("Ошибка: " + (error.data?.message || error.message));
            }
        }
    };

    const handleAddPercent = async () => {
        if (!dailyPercent) {
            window.alert("Введите процент");
            return;
        }
        try {
            await addPercent({
                daily_percent: parseFloat(dailyPercent),
                type: selectedStrategy,
            }).unwrap();
            setDailyPercent("");
            window.alert("Процент начислен успешно");
            refetchFinancialStats();
        } catch (error: any) {
            window.alert(
                "Ошибка: " +
                    (error.data?.message ||
                        error.message ||
                        "Неизвестная ошибка")
            );
        }
    };

    const handleConfirmInvestmentRequest = async (
        request: InvestmentWithdrawRequest
    ) => {
        const confirmMessage =
            `Подтвердить вывод для пользователя #${request.user_id} (стратегия ${request.strategy})?\n\n` +
            `Запрошено: ${request.pending_amount.toFixed(2)} USDT\n` +
            `Комиссия (1%): ${request.commission.toFixed(2)} USDT\n` +
            `К зачислению: ${request.net_amount.toFixed(2)} USDT`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        const key = `${request.user_id}-${request.strategy}`;
        setPendingInvestmentKey(key);

        try {
            await confirmInvestmentWithdraw({
                user_id: request.user_id,
                strategy: request.strategy,
            }).unwrap();
            window.alert("Вывод подтвержден");
            refetchInvestmentWithdrawals();
            refetchFinancialStats();
        } catch (error: any) {
            window.alert("Ошибка: " + (error.data?.message || error.message));
        } finally {
            setPendingInvestmentKey(null);
        }
    };

    const handleCancelInvestmentRequest = async (
        request: InvestmentWithdrawRequest
    ) => {
        const confirmMessage =
            `Отменить заявку на вывод для пользователя #${request.user_id} (стратегия ${request.strategy})?\n\n` +
            `Запрошено: ${request.pending_amount.toFixed(2)} USDT\n\n` +
            `Средства будут возвращены в стратегию ${request.strategy}.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        const comment = window.prompt("Введите комментарий к отмене (необязательно):") || undefined;

        const key = `${request.user_id}-${request.strategy}`;
        setPendingInvestmentKey(key);

        try {
            await cancelInvestmentWithdraw({
                user_id: request.user_id,
                strategy: request.strategy,
                comment,
            }).unwrap();
            window.alert("Заявка на вывод отменена");
            refetchInvestmentWithdrawals();
            refetchFinancialStats();
        } catch (error: any) {
            window.alert("Ошибка: " + (error.data?.message || error.message));
        } finally {
            setPendingInvestmentKey(null);
        }
    };

    const handleConfirmTransferRequest = async (
        request: import("../types/investments").InvestmentTransferRequest
    ) => {
        const confirmMessage =
            `Подтвердить перевод для пользователя #${request.user_id}?\n\n` +
            `Из стратегии ${request.from_strategy} в стратегию ${request.to_strategy}\n` +
            `Сумма: ${request.amount.toFixed(2)} USDT`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        const key = `${request.user_id}-${request.from_strategy}-${request.to_strategy}-${request.amount}`;
        setPendingTransferKey(key);

        try {
            await confirmInvestmentTransfer({
                user_id: request.user_id,
                from_strategy: request.from_strategy,
                to_strategy: request.to_strategy,
                amount: request.amount,
            }).unwrap();
            window.alert("Перевод подтвержден");
            refetchInvestmentTransfers();
            refetchFinancialStats();
        } catch (error: any) {
            window.alert("Ошибка: " + (error.data?.message || error.message));
        } finally {
            setPendingTransferKey(null);
        }
    };

    const handleCancelTransferRequest = async (
        request: import("../types/investments").InvestmentTransferRequest
    ) => {
        const confirmMessage =
            `Отменить перевод для пользователя #${request.user_id}?\n\n` +
            `Из стратегии ${request.from_strategy} в стратегию ${request.to_strategy}\n` +
            `Сумма: ${request.amount.toFixed(2)} USDT\n\n` +
            `Средства будут возвращены в стратегию ${request.from_strategy}.`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        const key = `${request.user_id}-${request.from_strategy}-${request.to_strategy}-${request.amount}`;
        setPendingTransferKey(key);

        try {
            await cancelInvestmentTransfer({
                user_id: request.user_id,
                from_strategy: request.from_strategy,
                to_strategy: request.to_strategy,
                amount: request.amount,
            }).unwrap();
            window.alert("Перевод отменен");
            refetchInvestmentTransfers();
            refetchFinancialStats();
        } catch (error: any) {
            window.alert("Ошибка: " + (error.data?.message || error.message));
        } finally {
            setPendingTransferKey(null);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Стейкинг
            </h1>

            <FinancialStats
                stats={stats}
                onConfirmWithdraw={handleConfirmWithdraw}
                onConfirmTransfer={handleConfirmTransfer}
                isConfirmingWithdraw={isConfirmingWithdraw}
                isConfirmingTransfer={isConfirmingTransfer}
            />

            <InvestmentManagement
                stats={stats}
                dailyPercent={dailyPercent}
                selectedStrategy={selectedStrategy}
                isAddingPercent={isAddingPercent}
                onDailyPercentChange={setDailyPercent}
                onStrategyChange={setSelectedStrategy}
                onAddPercent={handleAddPercent}
            />
            <InvestmentWithdrawRequests
                requests={investmentWithdrawRequests?.data?.requests}
                summary={investmentWithdrawRequests?.data?.summary}
                isLoading={isFetchingInvestmentWithdrawals}
                onRefresh={() => refetchInvestmentWithdrawals()}
                onConfirm={handleConfirmInvestmentRequest}
                onCancel={handleCancelInvestmentRequest}
                pendingKey={pendingInvestmentKey}
            />

            <InvestmentTransferRequests
                requests={investmentTransferRequests?.data?.requests}
                summary={investmentTransferRequests?.data?.summary}
                isLoading={isFetchingInvestmentTransfers}
                onRefresh={() => refetchInvestmentTransfers()}
                onConfirmAll={handleConfirmTransfer}
                onConfirm={handleConfirmTransferRequest}
                onCancel={handleCancelTransferRequest}
                isConfirming={isConfirmingTransfer}
                pendingKey={pendingTransferKey}
            />

            <InvestmentStrategiesAggregatedStats skip={skipAdmin} />
            <InvestmentStatsTabs skip={skipAdmin} />
        </div>
    );
};
